import { format, formatISO, Locale } from 'date-fns';

/**
 * Format a date object to "dd.MM.yyyy". This method is the default that should be used
 * for dates
 *
 * @param date The date to format
 */
const formatDate = (date: Date) => {
    return format(date, 'dd.MM.yyyy');
};

/**
 * Format a date object to "dd.MM.yyyy | EEEE".
 *
 * The EEEE will add a translation of the week day to the string e.g. Saturday, Thursday.
 *
 * @param date The date to format
 * @param fdsLocale The locale object of FDS library to use correct translation for the day
 */
const formatDateWithWeekday = (date: Date, fdsLocale: Locale) => {
    return format(date, 'dd.MM.yyyy | EEEE', {
        locale: fdsLocale
    });
};

/**
 * Format a date object to "HH:mm"
 *
 * @param date The date to format
 */
const formatTime = (date: Date) => {
    return format(date, 'HH:mm');
};

/**
 * Format two dates into a time range in the format of 'HH:mm - HH:mm'.
 *
 * If the end date is undefined, only the start date will be returned as 'HH:mm'.
 *
 * @param start The start date of the range.
 * @param end The optional end date of the range
 */
const formatTimeRange = (start: Date, end?: Date) => {
    return formatTime(start) + (end ? ' - ' + formatTime(end) : '');
};

/**
 * Format two dates into a date range in the format of 'dd.MM.yyyy - dd.MM.yyyy'.
 *
 * If the end date is undefined, only the start date will be returned as 'dd.MM.yyyy'.
 *
 * @param start The start date of the range.
 * @param end The optional end date of the range
 */
const formatDateRange = (start: Date, end?: Date) => {
    return formatDate(start) + (end ? ' - ' + formatDate(end) : '');
};

/**
 * Format a date to "dd.MM.yyyy HH:mm"
 *
 * @param date The date to format
 */
const formatDateTimeString = (date: Date) => {
    return format(date, 'dd.MM.yyyy HH:mm');
};

/**
 * ISO representation of a date without time information stored in the string. Use this method
 * in order to send a value to a date only field in strapi, as strapi will just cut the time portion
 * off the string when sending the full date time represenation which erases potential timezone
 * information and stores wrong values.
 *
 * The format is yyyy-MM-dd e.g. 2023-10-25
 */
const formatIsoDate = (date: Date) => {
    return formatISO(date, { representation: 'date' });
};

export {
    formatDate,
    formatDateRange,
    formatDateWithWeekday,
    formatTime,
    formatTimeRange,
    formatDateTimeString,
    formatIsoDate
};
